import axios from 'axios';
import { getAuth } from "firebase/auth";
import { logger } from '../utils/logger';
import { baseAxiosConfig, applyVerticalOverrideInterceptor } from '../utils/apiConfig';
import { getReviews } from './reviewService';

const pricingAxios = applyVerticalOverrideInterceptor(axios.create(baseAxiosConfig));

/**
 * Pricing Service - Business Pricing Analysis
 *
 * Provides pricing tier analysis and rankings for businesses:
 * - Price Level: 1-4 ($-$$$$)
 * - Value Score: Rating vs Price analysis
 * - Market Distribution: How businesses distribute across price tiers
 * - Pricing Sentiment: Review-derived pricing perception
 */

/**
 * Price tier definitions
 */
export const PRICE_TIERS = {
  1: { label: '$', description: 'Budget-friendly', color: 'green', minSpend: 0, maxSpend: 15 },
  2: { label: '$$', description: 'Moderate', color: 'blue', minSpend: 15, maxSpend: 35 },
  3: { label: '$$$', description: 'Upscale', color: 'purple', minSpend: 35, maxSpend: 60 },
  4: { label: '$$$$', description: 'Premium', color: 'orange', minSpend: 60, maxSpend: null },
};

// Keywords for pricing sentiment analysis - must match backend
// Positive signals - customers feel pricing is good
export const PRICING_POSITIVE_KEYWORDS = {
  value_words: [
    "affordable", "cheap", "reasonable", "fair", "economical", 
    "budget-friendly", "budget friendly", "inexpensive", "low-cost",
    "bargain", "great price", "good price", "fair price"
  ],
  worth_words: [
    "worth it", "great value", "good deal", "bang for buck", 
    "bang for your buck", "value for money", "money well spent",
    "good value", "excellent value", "amazing value", "fantastic value",
    "worth every penny", "worth the money", "worth the price"
  ],
  comparison_words: [
    "competitive", "competitive price", "competitive pricing",
    "lower than", "better price", "better deal", "best price",
    "cheaper than", "more affordable", "best value"
  ]
};

// Negative signals - customers feel pricing is bad
export const PRICING_NEGATIVE_KEYWORDS = {
  expensive_words: [
    "expensive", "costly", "pricey", "high-priced", "premium price",
    "steep", "steep price", "high price", "high cost"
  ],
  excess_words: [
    "overpriced", "too much", "outrageous", "ridiculous",
    "rip off", "rip-off", "ripoff", "gouging", "price gouging",
    "highway robbery", "overcharge", "overcharged", "way too expensive",
    "way too much", "absurdly expensive", "insanely expensive"
  ],
  regret_words: [
    "not worth it", "not worth", "waste of money", "waste money",
    "could have been cheaper", "could've been cheaper",
    "should be cheaper", "too expensive for", "not worth the price",
    "didn't justify", "doesn't justify"
  ]
};

// Get all positive keywords as flat array
export const getAllPositiveKeywords = () => [
  ...PRICING_POSITIVE_KEYWORDS.value_words,
  ...PRICING_POSITIVE_KEYWORDS.worth_words,
  ...PRICING_POSITIVE_KEYWORDS.comparison_words
];

// Get all negative keywords as flat array
export const getAllNegativeKeywords = () => [
  ...PRICING_NEGATIVE_KEYWORDS.expensive_words,
  ...PRICING_NEGATIVE_KEYWORDS.excess_words,
  ...PRICING_NEGATIVE_KEYWORDS.regret_words
];

/**
 * Analyze text for pricing sentiment signals
 * Returns detailed analysis including which keywords were found
 */
function analyzePricingSentiment(text) {
  if (!text) return { positive: 0, negative: 0, sentiment: null, positiveKeywords: [], negativeKeywords: [] };
  
  const textLower = text.toLowerCase();
  const allPositive = getAllPositiveKeywords();
  const allNegative = getAllNegativeKeywords();
  
  const positiveKeywords = allPositive.filter(kw => textLower.includes(kw));
  const negativeKeywords = allNegative.filter(kw => textLower.includes(kw));
  
  const positiveCount = positiveKeywords.length;
  const negativeCount = negativeKeywords.length;
  
  let sentiment = null;
  if (positiveCount > negativeCount) sentiment = 'positive';
  else if (negativeCount > positiveCount) sentiment = 'negative';
  else if (positiveCount > 0 || negativeCount > 0) sentiment = 'mixed';
  
  return { 
    positive: positiveCount, 
    negative: negativeCount, 
    sentiment,
    positiveKeywords,
    negativeKeywords
  };
}

/**
 * Highlight pricing keywords in text
 * @param {string} text - Text to highlight
 * @param {string} keyword - Keyword to highlight
 * @returns {Object} - { before, keyword, after } parts of text
 */
export function highlightKeywordInContext(text, keyword) {
  if (!text || !keyword) return { before: text, keyword: '', after: '' };
  
  const lowerText = text.toLowerCase();
  const lowerKeyword = keyword.toLowerCase();
  const index = lowerText.indexOf(lowerKeyword);
  
  if (index === -1) return { before: text, keyword: '', after: '' };
  
  return {
    before: text.slice(0, index),
    keyword: text.slice(index, index + keyword.length),
    after: text.slice(index + keyword.length)
  };
}

/**
 * Get sentiment label and color for display
 * @param {string} sentiment - Sentiment type (STRONG_POSITIVE, MILD_POSITIVE, etc.)
 * @returns {Object} - { label, color, icon }
 */
export function getSentimentDisplay(sentiment) {
  const sentimentMap = {
    'STRONG_POSITIVE': { label: 'Very Positive', color: 'green', score: 2 },
    'MILD_POSITIVE': { label: 'Positive', color: 'green', score: 1 },
    'NEUTRAL': { label: 'Neutral', color: 'gray', score: 0 },
    'MILD_NEGATIVE': { label: 'Negative', color: 'red', score: -1 },
    'STRONG_NEGATIVE': { label: 'Very Negative', color: 'red', score: -2 }
  };
  return sentimentMap[sentiment] || { label: 'Unknown', color: 'gray', score: 0 };
}

/**
 * Estimate price level for a business based on available signals
 * Uses multiple factors to create good differentiation across all 4 tiers
 */
function estimatePriceLevel(business, allBusinesses = []) {
  // Check for direct Google Places price_level
  if (business.price_level) {
    return {
      price_level: Math.min(4, Math.max(1, business.price_level)),
      source: 'google_places',
      confidence: 0.95
    };
  }
  
  const primaryType = (business.primary_type || '').toLowerCase();
  const types = (business.types || []).map(t => t.toLowerCase());
  const name = (business.name || '').toLowerCase();
  
  let score = 50; // Start at middle (scale 0-100, will map to 1-4)
  let confidence = 0.5;
  
  // === PREMIUM INDICATORS (increase score) ===
  const premiumKeywords = [
    'fine_dining', 'spa', 'luxury', 'premium', 'boutique', 'upscale', 
    'gourmet', 'steakhouse', 'prime', 'elite', 'exclusive', 'artisan',
    'craft', 'organic', 'farm-to-table', 'michelin', 'award'
  ];
  const premiumNameWords = [
    'bistro', 'grill', 'steakhouse', 'prime', 'luxe', 'elite', 
    'artisan', 'craft', 'boutique', 'gallery', 'estate', 'reserve'
  ];
  
  // === BUDGET INDICATORS (decrease score) ===
  const budgetKeywords = [
    'fast_food', 'quick_service', 'budget', 'discount', 'dollar',
    'convenience', 'takeout', 'delivery', 'counter_service', 'cafeteria'
  ];
  const budgetNameWords = [
    'express', 'quick', 'fast', 'dollar', 'discount', 'budget',
    'cheap', 'value', 'deals', 'combo', 'special', '99', 'mart'
  ];
  
  // Check business type keywords
  if (premiumKeywords.some(kw => primaryType.includes(kw) || types.some(t => t.includes(kw)))) {
    score += 25;
    confidence = 0.7;
  }
  if (budgetKeywords.some(kw => primaryType.includes(kw) || types.some(t => t.includes(kw)))) {
    score -= 25;
    confidence = 0.7;
  }
  
  // Check business name
  if (premiumNameWords.some(word => name.includes(word))) {
    score += 15;
  }
  if (budgetNameWords.some(word => name.includes(word))) {
    score -= 15;
  }
  
  // === RATING-BASED ADJUSTMENT ===
  // Very high ratings often correlate with premium pricing
  const ratingAvg = business.rating_avg;
  if (ratingAvg) {
    if (ratingAvg >= 4.7) {
      score += 20; // Exceptional rating = likely premium
    } else if (ratingAvg >= 4.4) {
      score += 10;
    } else if (ratingAvg < 3.5) {
      score -= 15; // Low rating = likely budget/value
    } else if (ratingAvg < 4.0) {
      score -= 5;
    }
  }
  
  // === REVIEW VOLUME ADJUSTMENT ===
  // Very established places with tons of reviews often can charge more
  const ratingCount = business.rating_count;
  if (ratingCount) {
    if (ratingCount > 1000) {
      score += 15; // Very established
    } else if (ratingCount > 500) {
      score += 8;
    } else if (ratingCount < 20) {
      score -= 10; // New/small = often budget-friendly
    } else if (ratingCount < 50) {
      score -= 5;
    }
  }
  
  // === RELATIVE RANKING within market ===
  // Use percentile ranking to ensure distribution
  if (allBusinesses.length > 5) {
    // Calculate a composite score for all businesses
    const getCompositeScore = (b) => {
      let s = 0;
      if (b.rating_avg) s += b.rating_avg * 10;
      if (b.rating_count) s += Math.log10(b.rating_count + 1) * 5;
      return s;
    };
    
    const thisScore = getCompositeScore(business);
    const allScores = allBusinesses.map(getCompositeScore).sort((a, b) => a - b);
    const percentile = allScores.filter(s => s <= thisScore).length / allScores.length;
    
    // Adjust based on percentile (top businesses = premium, bottom = budget)
    if (percentile >= 0.9) {
      score += 15; // Top 10%
    } else if (percentile >= 0.75) {
      score += 8; // Top 25%
    } else if (percentile <= 0.1) {
      score -= 15; // Bottom 10%
    } else if (percentile <= 0.25) {
      score -= 8; // Bottom 25%
    }
  }
  
  // === MAP SCORE TO PRICE LEVEL ===
  // Score 0-100 maps to levels 1-4
  // Use thresholds that create good distribution
  let priceLevel;
  if (score >= 70) {
    priceLevel = 4; // $$$$
  } else if (score >= 50) {
    priceLevel = 3; // $$$
  } else if (score >= 30) {
    priceLevel = 2; // $$
  } else {
    priceLevel = 1; // $
  }
  
  return {
    price_level: priceLevel,
    source: 'estimated',
    confidence: Math.min(0.8, confidence),
    _score: score // For debugging
  };
}

/**
 * Get pricing dashboard data - uses reviews endpoint and derives pricing locally
 * @returns {Promise<Object>} Pricing dashboard response with businesses and market summary
 */
export async function getPricingDashboard() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    logger.log('Fetching reviews data for pricing analysis');

    // Try the dedicated pricing endpoint first
    try {
      const token = await user.getIdToken();
      const response = await pricingAxios.get('/pricing/', {
        params: { user: user.email },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data?.businesses) {
        logger.log('Pricing data loaded from server');
        return response.data;
      }
    } catch (apiError) {
      logger.log('Pricing endpoint not available, deriving from reviews data');
    }

    // Fallback: derive pricing from reviews data
    const reviewsData = await getReviews();
    
    if (!reviewsData || reviewsData.length === 0) {
      throw new Error('No business data available');
    }

    // Process each business to derive pricing
    const tierCounts = { '$': 0, '$$': 0, '$$$': 0, '$$$$': 0 };
    const tierRatings = { '$': [], '$$': [], '$$$': [], '$$$$': [] };
    const tierReviews = { '$': [], '$$': [], '$$$': [], '$$$$': [] };
    
    // First pass: estimate all price levels with relative ranking
    const businesses = reviewsData.map(business => {
      const estimation = estimatePriceLevel(business, reviewsData);
      return { ...business, _estimation: estimation };
    });
    
    // Calculate industry average price level
    let totalLevel = 0;
    businesses.forEach(b => { totalLevel += b._estimation.price_level; });
    const industryAvg = businesses.length > 0 ? totalLevel / businesses.length : 2.0;
    
    const pricingResults = businesses.map(business => {
      const estimation = business._estimation;
      const priceLevel = estimation.price_level;
      const priceLabel = '$'.repeat(priceLevel);
      
      // Analyze pricing sentiment from review insights
      const reviewText = [
        business.insight || '',
        business.versus_insights || '',
        business.market_trends_insight || '',
      ].join(' ');
      const sentiment = analyzePricingSentiment(reviewText);
      
      // Determine price vs industry
      let priceVsIndustry = 'average';
      if (priceLevel < industryAvg - 0.5) priceVsIndustry = 'below';
      else if (priceLevel > industryAvg + 0.5) priceVsIndustry = 'above';
      
      // Update tier statistics
      if (tierCounts[priceLabel] !== undefined) {
        tierCounts[priceLabel]++;
        if (business.rating_avg) tierRatings[priceLabel].push(business.rating_avg);
        if (business.rating_count) tierReviews[priceLabel].push(business.rating_count);
      }
      
      return {
        name: business.name,
        maps_uri: business.maps_uri,
        price_level: priceLevel,
        price_label: priceLabel,
        price_source: estimation.source,
        confidence_score: estimation.confidence,
        rating_avg: business.rating_avg,
        rating_count: business.rating_count,
        pricing_mentions_positive: sentiment.positive,
        pricing_mentions_negative: sentiment.negative,
        pricing_sentiment: sentiment.sentiment,
        industry_avg_price_level: Math.round(industryAvg * 100) / 100,
        price_vs_industry: priceVsIndustry,
        website_uri: business.website_uri,
        address: business.address,
        primary_type: business.primary_type,
      };
    });
    
    // Sort by price level then by rating
    pricingResults.sort((a, b) => a.price_level - b.price_level || (b.rating_avg || 0) - (a.rating_avg || 0));
    
    // Calculate average rating per tier
    const avgRatingByTier = {};
    const avgReviewsByTier = {};
    Object.entries(tierRatings).forEach(([tier, ratings]) => {
      avgRatingByTier[tier] = ratings.length > 0 
        ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10
        : 0;
    });
    Object.entries(tierReviews).forEach(([tier, reviews]) => {
      avgReviewsByTier[tier] = reviews.length > 0 
        ? Math.round(reviews.reduce((a, b) => a + b, 0) / reviews.length)
        : 0;
    });
    
    const marketSummary = {
      total_businesses: reviewsData.length,
      businesses_with_pricing: pricingResults.length,
      tier_distribution: tierCounts,
      avg_price_level: Math.round(industryAvg * 100) / 100,
      premium_businesses: pricingResults.filter(b => b.price_level === 4).slice(0, 5).map(b => b.name),
      budget_friendly: pricingResults.filter(b => b.price_level === 1).slice(0, 5).map(b => b.name),
      avg_rating_by_tier: avgRatingByTier,
      avg_reviews_by_tier: avgReviewsByTier,
    };

    const pricingTiers = [
      { level: 1, label: '$', description: 'Budget-friendly, affordable options', min_typical_spend: 0, max_typical_spend: 15 },
      { level: 2, label: '$$', description: 'Moderate pricing, good value', min_typical_spend: 15, max_typical_spend: 35 },
      { level: 3, label: '$$$', description: 'Upscale, higher-end options', min_typical_spend: 35, max_typical_spend: 60 },
      { level: 4, label: '$$$$', description: 'Premium, luxury establishments', min_typical_spend: 60, max_typical_spend: null },
    ];

    logger.log('Pricing analysis completed:', {
      businesses: pricingResults.length,
      avgPriceLevel: industryAvg,
    });

    return {
      businesses: pricingResults,
      market_summary: marketSummary,
      pricing_tiers: pricingTiers,
      last_calculated: new Date().toISOString(),
    };
  } catch (error) {
    logger.error("Error in getPricingDashboard:", error);
    throw error;
  }
}

/**
 * Get detailed pricing perception for a single business
 * @param {string} mapsUri - Business maps_uri identifier
 * @returns {Promise<Object>} Detailed pricing perception with mentions, keywords, market rank
 */
export async function getBusinessPricing(mapsUri) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const response = await pricingAxios.get('/pricing/business', {
      params: { maps_uri: mapsUri, user: user.email },
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    logger.error('Error fetching business pricing:', error);
    return null;
  }
}

/**
 * Update price level for a specific business
 * @param {string} mapsUri - Business maps_uri identifier
 * @param {number} priceLevel - New price level (1-4)
 * @returns {Promise<Object>} Update result
 */
export async function updateBusinessPricing(mapsUri, priceLevel) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const response = await pricingAxios.put(
      '/pricing/price-level',
      null,
      {
        params: {
          maps_uri: mapsUri,
          user: user.email,
          price_level: priceLevel
        },
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      }
    );

    logger.log('Business pricing updated:', { mapsUri, priceLevel });
    return response.data;
  } catch (error) {
    logger.error("Error updating business pricing:", error);
    throw error;
  }
}

/**
 * Get price tier info for a given level
 * @param {number} level - Price level (1-4)
 * @returns {Object} Tier info with label, description, color
 */
export function getPriceTierInfo(level) {
  return PRICE_TIERS[level] || PRICE_TIERS[2];
}

/**
 * Get color for price level (Chakra UI color scheme)
 * @param {number} level - Price level (1-4)
 * @returns {string} Chakra UI color scheme name
 */
export function getPriceLevelColor(level) {
  const colors = {
    1: 'green',
    2: 'blue', 
    3: 'purple',
    4: 'orange'
  };
  return colors[level] || 'gray';
}

/**
 * Get gradient color for price level (hex)
 * @param {number} level - Price level (1-4)
 * @returns {string} Hex color for gradients
 */
export function getPriceLevelHexColor(level) {
  const colors = {
    1: '#38A169', // green.500
    2: '#3182CE', // blue.500
    3: '#805AD5', // purple.500
    4: '#DD6B20'  // orange.500
  };
  return colors[level] || '#718096';
}

/**
 * Format price level for display
 * @param {number} level - Price level (1-4)
 * @returns {string} Dollar signs ($ to $$$$)
 */
export function formatPriceLevel(level) {
  if (!level || level < 1 || level > 4) return 'N/A';
  return '$'.repeat(level);
}

/**
 * Format currency value
 * @param {number} value - Dollar amount
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value) {
  if (!value && value !== 0) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Calculate pricing insights from dashboard data
 * @param {Object} dashboardData - Pricing dashboard response
 * @returns {Object} Calculated insights
 */
export function calculatePricingInsights(dashboardData) {
  if (!dashboardData?.businesses?.length) {
    return null;
  }

  const businesses = dashboardData.businesses;
  const summary = dashboardData.market_summary;

  // Group by price tier
  const tierGroups = {
    '$': businesses.filter(b => b.price_level === 1),
    '$$': businesses.filter(b => b.price_level === 2),
    '$$$': businesses.filter(b => b.price_level === 3),
    '$$$$': businesses.filter(b => b.price_level === 4),
  };

  // Calculate average rating per tier
  const avgRatingByTier = {};
  Object.entries(tierGroups).forEach(([tier, group]) => {
    const ratings = group.filter(b => b.rating_avg).map(b => b.rating_avg);
    avgRatingByTier[tier] = ratings.length > 0 
      ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1)
      : null;
  });

  return {
    totalBusinesses: businesses.length,
    avgPriceLevel: summary?.avg_price_level,
    tierDistribution: summary?.tier_distribution || {},
    premiumBusinesses: tierGroups['$$$$'],
    budgetFriendly: tierGroups['$'],
    avgRatingByTier,
    pricingCorrelation: summary?.avg_rating_by_tier,
  };
}

// Export all functions
const pricingService = {
  getPricingDashboard,
  getBusinessPricing,
  updateBusinessPricing,
  getPriceTierInfo,
  getPriceLevelColor,
  getPriceLevelHexColor,
  formatPriceLevel,
  formatCurrency,
  calculatePricingInsights,
  highlightKeywordInContext,
  getSentimentDisplay,
  getAllPositiveKeywords,
  getAllNegativeKeywords,
  PRICE_TIERS,
  PRICING_POSITIVE_KEYWORDS,
  PRICING_NEGATIVE_KEYWORDS,
};

export default pricingService;
